import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Upload, ArrowRight, Users, Eye, Monitor, Smartphone, Tablet } from "lucide-react";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";

function Stat({ label, value, testId }) {
  return (
    <div className="bg-white border border-[#E7D8C6] p-6" data-testid={testId}>
      <div className="text-4xl font-sans font-light tabular-nums">{value ?? "–"}</div>
      <div className="text-xs uppercase tracking-[0.15em] text-[#9A86B4] mt-2">{label}</div>
    </div>
  );
}

function VisitorTile({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-[#E7D8C6] p-5 flex items-center gap-4" data-testid={testId}>
      <div className="w-11 h-11 rounded-full bg-[#F1E4FA] text-[#632A8E] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div>
        <div className="text-2xl font-light tabular-nums leading-none">{value ?? "–"}</div>
        <div className="text-xs uppercase tracking-[0.12em] text-[#9A86B4] mt-1.5">{label}</div>
      </div>
    </div>
  );
}

const DEV_META = {
  desktop: { label: "Desktop", icon: Monitor },
  mobile: { label: "Mobil", icon: Smartphone },
  tablet: { label: "Tablet", icon: Tablet },
};

export default function Dashboard() {
  const [d, setD] = useState({});
  const [s, setS] = useState(null);
  const [range, setRange] = useState(30);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/admin/stats?days=${range}`).then(({ data }) => setS(data)).catch(() => {});
  }, [range]);

  const chartData = (s?.series || []).map((p) => ({
    date: p.date.slice(5),
    Besucher: p.visitors,
    Aufrufe: p.views,
  }));
  const devTotal = s ? Object.values(s.devices || {}).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="fade-in">
      <h1 className="font-serif text-4xl font-light mb-8">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Kunstwerke" value={d.total_artworks} testId="stat-artworks" />
        <Stat label="Verfügbar" value={d.available} testId="stat-available" />
        <Stat label="Verkauft" value={d.sold} testId="stat-sold" />
        <Stat label="Entwürfe (Kunst)" value={d.artwork_drafts} testId="stat-art-drafts" />
        <Stat label="Gedichte online" value={d.poems_published} testId="stat-poems" />
        <Stat label="Entwürfe (Gedichte)" value={d.poem_drafts} testId="stat-poem-drafts" />
        <Stat label="Neue Anfragen" value={d.unread_requests} testId="stat-unread" />
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-12">
        <Link to="/admin/kunstwerke/neu" data-testid="quick-new-artwork" className="flex items-center gap-3 bg-[#F4C542] text-[#32104F] px-5 py-4 hover:bg-[#D85CA4] transition-colors">
          <Plus className="w-5 h-5" /> Neues Kunstwerk
        </Link>
        <Link to="/admin/gedichte/neu" data-testid="quick-new-poem" className="flex items-center gap-3 bg-[#F4C542] text-[#32104F] px-5 py-4 hover:bg-[#D85CA4] transition-colors">
          <Plus className="w-5 h-5" /> Neues Gedicht
        </Link>
        <Link to="/admin/medien" data-testid="quick-media" className="flex items-center gap-3 border border-brand text-brand px-5 py-4 hover:bg-brand hover:text-white transition-colors">
          <Upload className="w-5 h-5" /> Bilder hochladen
        </Link>
      </div>

      {/* Besucherstatistik */}
      <section className="mb-12" data-testid="visitor-stats">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-serif text-2xl">Besucher</h2>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-md border border-[#E7D8C6] overflow-hidden" data-testid="stats-range-toggle">
              {[7, 30, 90].map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  data-testid={`stats-range-${r}`}
                  className={`px-3.5 py-1.5 text-sm transition-colors ${range === r ? "bg-[#632A8E] text-white" : "bg-white text-[#6E5A82] hover:bg-[#F5EBDB]"}`}
                >
                  {r} Tage
                </button>
              ))}
            </div>
            <span className="text-xs text-[#9A86B4] hidden sm:inline">anonym · ohne Cookies</span>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <VisitorTile icon={Users} label="Besucher heute" value={s?.visitors_today} testId="vs-visitors-today" />
          <VisitorTile icon={Users} label="Gestern" value={s?.visitors_yesterday} testId="vs-visitors-yesterday" />
          <VisitorTile icon={Users} label={`Besucher (${range} Tage)`} value={s?.visitors_period} testId="vs-visitors-period" />
          <VisitorTile icon={Eye} label={`Aufrufe (${range} Tage)`} value={s?.views_period} testId="vs-views-period" />
        </div>

        <div className="bg-white border border-[#E7D8C6] p-5 mb-6" data-testid="vs-chart">
          <div className="text-xs uppercase tracking-[0.12em] text-[#9A86B4] mb-4">Letzte {range} Tage</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#9A4DCC" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#9A4DCC" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gView" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F4C542" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#F4C542" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0E6D6" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9A86B4" }} interval={range <= 7 ? 0 : range <= 30 ? 4 : 9} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E7D8C6", fontSize: 13 }} />
                <Area type="monotone" dataKey="Besucher" stroke="#632A8E" strokeWidth={2} fill="url(#gVis)" />
                <Area type="monotone" dataKey="Aufrufe" stroke="#D8A72E" strokeWidth={2} fill="url(#gView)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E7D8C6] p-5" data-testid="vs-top-artworks">
            <div className="text-xs uppercase tracking-[0.12em] text-[#9A86B4] mb-3">Top Kunstwerke</div>
            <TopList items={s?.top_artworks} to="/galerie" empty="Noch keine Aufrufe" />
          </div>
          <div className="bg-white border border-[#E7D8C6] p-5" data-testid="vs-top-poems">
            <div className="text-xs uppercase tracking-[0.12em] text-[#9A86B4] mb-3">Top Gedichte</div>
            <TopList items={s?.top_poems} to="/gedichte" empty="Noch keine Aufrufe" />
          </div>
          <div className="bg-white border border-[#E7D8C6] p-5" data-testid="vs-devices">
            <div className="text-xs uppercase tracking-[0.12em] text-[#9A86B4] mb-3">Geräte ({range} Tage)</div>
            {devTotal ? (
              <ul className="space-y-3">
                {Object.entries(s.devices).map(([k, v]) => {
                  const meta = DEV_META[k] || { label: k, icon: Monitor };
                  const pct = devTotal ? Math.round((v / devTotal) * 100) : 0;
                  const Icon = meta.icon;
                  return (
                    <li key={k}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-2 text-[#3A1E55]"><Icon className="w-4 h-4 text-[#9A86B4]" /> {meta.label}</span>
                        <span className="tabular-nums text-[#6E5A82]">{pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-[#F1E4FA] overflow-hidden">
                        <div className="h-full bg-[#9A4DCC]" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-[#9A86B4]">Noch keine Daten.</p>
            )}
            {s?.referrers?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[#F5EBDB]">
                <div className="text-xs uppercase tracking-[0.12em] text-[#9A86B4] mb-2">Herkunft</div>
                <ul className="space-y-1 text-sm">
                  {s.referrers.slice(0, 4).map((r) => (
                    <li key={r.domain} className="flex justify-between text-[#6E5A82]"><span className="truncate">{r.domain}</span><span className="tabular-nums">{r.count}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="bg-white border border-[#E7D8C6] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">Letzte Anfragen</h2>
          <Link to="/admin/anfragen" className="text-sm text-[#6E5A82] hover:text-ink inline-flex items-center gap-1">Alle <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {d.recent_requests?.length ? (
          <ul className="divide-y divide-[#F5EBDB]">
            {d.recent_requests.map((r) => (
              <li key={r.id} className="py-3 flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-sm">{r.name} {r.artwork_title && <span className="text-[#9A86B4]">· {r.artwork_title}</span>}</div>
                  <div className="text-xs text-[#9A86B4] line-clamp-1">{r.message}</div>
                </div>
                {!r.read && <span className="text-[10px] uppercase tracking-wide bg-[#F4C542] text-[#32104F] px-2 py-0.5">Neu</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[#9A86B4]">Noch keine Anfragen.</p>
        )}
      </div>
    </div>
  );
}

function TopList({ items, to, empty }) {
  if (!items?.length || items.every((i) => !i.views)) return <p className="text-sm text-[#9A86B4]">{empty}</p>;
  return (
    <ul className="space-y-2">
      {items.filter((i) => i.views).map((i) => (
        <li key={i.slug} className="flex items-center justify-between gap-3 text-sm">
          <Link to={`${to}/${i.slug}`} className="truncate text-[#3A1E55] hover:text-[#632A8E]">{i.title}</Link>
          <span className="tabular-nums text-[#6E5A82] shrink-0">{i.views}</span>
        </li>
      ))}
    </ul>
  );
}
